import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { RowSelectionState, DropdownItem, M, Button } from 'design-system-web';
import { Dropdown } from 'components';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { bulkUpdateServiceRequestStatusMutation } from 'graphql-queries';
import { ServiceRequestStatusEnum } from 'domains/workOrder/serviceRequest/components/types/serviceRequestStatus';
import { getServiceRequestStatusLabel } from 'domains-work-order-type-contracts';

const SERVICE_REQUEST_SELECTION_LIMIT = 100;

type Props = {
  rowSelection?: RowSelectionState;
  refetch?: () => void;
};

export function BulkStatusUpdateAction({ rowSelection, refetch }: Props) {
  const { t } = useTranslation();
  const [bulkUpdateServiceRequestStatus] = useMutation(
    bulkUpdateServiceRequestStatusMutation
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const selectedWorkOrderIds = useMemo(() => {
    if (!rowSelection) {
      return [];
    }

    return Object.entries(rowSelection)
      .filter(([, isSelected]) => isSelected)
      .map(([workOrderId]) => workOrderId);
  }, [rowSelection]);

  const openModal = async () => {
    if (selectedWorkOrderIds.length === 0) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.updateStatus.noSelection')
      );

      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.updateStatus.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );

      return;
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedStatus('');
  };

  const handleConfirm = async () => {
    if (!selectedStatus) {
      return;
    }

    if (selectedWorkOrderIds.length > SERVICE_REQUEST_SELECTION_LIMIT) {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.updateStatus.limitExceeded', {
          serviceRequestSelectionLimit: SERVICE_REQUEST_SELECTION_LIMIT,
          selectedWorkOrderCount: selectedWorkOrderIds.length,
        }),
        5000
      );

      closeModal();

      return;
    }

    setLoading(true);

    try {
      const result = await bulkUpdateServiceRequestStatus({
        variables: {
          serviceRequestIds: selectedWorkOrderIds,
          status: selectedStatus,
        },
      });

      const { successCount, failureCount } = result.data
        ?.bulkUpdateServiceRequestStatus || {
        successCount: 0,
        failureCount: selectedWorkOrderIds.length,
      };

      if (failureCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.updateStatus.success', {
            count: successCount,
          })
        );
      } else if (successCount === 0) {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.updateStatus.error')
        );
      } else {
        await window.Toast.show(
          t('web.admin.serviceRequest.bulkActions.updateStatus.partial', {
            successCount,
            failureCount,
          }),
          5000
        );
      }

      if (refetch) {
        refetch();
      }
    } catch {
      await window.Toast.show(
        t('web.admin.serviceRequest.bulkActions.updateStatus.error')
      );
    }

    setLoading(false);
    closeModal();
  };

  const statusItems: DropdownItem<string>[] = [
    ServiceRequestStatusEnum.Created,
    ServiceRequestStatusEnum.Accepted,
    ServiceRequestStatusEnum.InProgress,
    ServiceRequestStatusEnum.OnHold,
    ServiceRequestStatusEnum.Complete,
    ServiceRequestStatusEnum.Closed,
    ServiceRequestStatusEnum.Declined,
    ServiceRequestStatusEnum.Cancelled,
  ].map(status => ({
    label: getServiceRequestStatusLabel(status) || status,
    value: status,
  }));

  return (
    <>
      <Button
        variant="secondary"
        size="large"
        onClick={openModal}
        testId="bulk-status-update-button"
      >
        {t('web.admin.serviceRequest.bulkActions.updateStatus.selectStatus')}
      </Button>

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        handleConfirm={handleConfirm}
        title={t(
          'web.admin.serviceRequest.bulkActions.updateStatus.confirm.title'
        )}
        confirmText={t(
          'web.admin.serviceRequest.bulkActions.updateStatus.confirm.button'
        )}
        cancelText={t(
          'web.admin.serviceRequest.bulkActions.updateStatus.cancel.button'
        )}
        loading={loading}
        confirmActionDisabled={!selectedStatus || loading}
        cancelActionDisabled={loading}
        isFullWidth
      >
        <M>
          {t(
            'web.admin.serviceRequest.bulkActions.updateStatus.confirm.message',
            {
              count: selectedWorkOrderIds.length,
            }
          )}
        </M>
        <Dropdown
          isSearchable
          value={selectedStatus}
          items={statusItems}
          onChange={(item: DropdownItem<string>) => {
            if (item.value) {
              setSelectedStatus(item.value);
            }
          }}
          placeholder={t(
            'web.admin.serviceRequest.bulkActions.updateStatus.selectStatus'
          )}
          doTranslation={false}
        />
      </ConfirmationModal>
    </>
  );
}
